import type PrintPriceCalculator from '@shared/utils/print-price/PrintPriceCalculator';
import type { PriceSummaryData, PriceSummaryLineItem } from './PriceSummary.types';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryDiscountInfo, StationeryPriceInfoRawLineItem, StationeryProduct } from '@shared/utils/print-price/types';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer';
import { useGetCardDeliveryInfo } from './utils/useGetCardDeliveryInfo';
import type { CardPromotion } from './PriceSummary.controller';
import { usePriceCalculatorLineItemsToPriceSummaryLineItems } from './utils/usePriceCalculatorLineItemsToPriceSummaryLineItems';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { PriceSummaryCurrencyFormatOptions } from './PriceSummary.constants';

export { PriceSummaryCurrencyFormatOptions } from './PriceSummary.constants';

type LineItemStep = {
  stepAlias: string;
  targetSteps: string[];
  shouldIncludeQuantity?: boolean;
};

type ValidLineItems = {
  card: PriceSummaryLineItem[];
  back: PriceSummaryLineItem[];
  shipping: PriceSummaryLineItem[];
  tax: PriceSummaryLineItem[];
  envelope: PriceSummaryLineItem[];
};

const lineItemSteps: LineItemStep[] = [
  {
    stepAlias: 'card',
    targetSteps: ['cardFront'],
    shouldIncludeQuantity: true,
  },
  {
    stepAlias: 'back',
    targetSteps: ['cardBack'],
  },
  {
    stepAlias: 'shipping',
    targetSteps: ['shipping'],
  },
  {
    stepAlias: 'tax',
    targetSteps: ['salesTax'],
  },
  {
    stepAlias: 'envelope',
    targetSteps: ['envelope', 'recipientAddress'],
  },
];

/**
 * Converts raw line items into grouped and translated line items.
 */
const useLineItemsByStep = (product: StationeryProduct, steps: LineItemStep[], items: StationeryPriceInfoRawLineItem[]): ValidLineItems => {
  const toLineItems = usePriceCalculatorLineItemsToPriceSummaryLineItems(product);

  const output = steps.reduce((acc, step) => {
    const { stepAlias, targetSteps, shouldIncludeQuantity } = step;
    const lineItemsForStep = toLineItems(items, targetSteps, shouldIncludeQuantity);
    return { ...acc, [stepAlias]: lineItemsForStep };
  }, {});

  return output as ValidLineItems;
};

export const cardPromotionToDiscount = (promotion: CardPromotion | undefined): StationeryDiscountInfo | undefined => {
  if (!promotion) {
    return undefined;
  }

  if (promotion.discountPercentage) {
    return {
      type: 'percent' as const,
      promoCode: promotion.code,
      percentage: promotion.discountPercentage,
    };
  }

  if (promotion.discountAmountInMinorUnits) {
    return {
      type: 'flatDiscountAmount' as const,
      promoCode: promotion.code,
      amount: promotion.discountAmountInMinorUnits,
    };
  }

  return undefined;
};

// New PriceSummaryController based on PrintPriceCalculator
// and new Product / Attribute / Option pricing structure
export const useProductPriceSummaryController = (
  priceCalculator: PrintPriceCalculator,
  cardConfig: ThemeJson,
  product: StationeryProduct,
  options: { attributeId: string; optionId: string }[],
  includeShipping: boolean,
  salesTax: Maybe<number>,
  promotion?: CardPromotion,
): PriceSummaryData => {
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const deliveryInfo = useGetCardDeliveryInfo(cardConfig);

  const { orderCurrencyISO4217: orderCurrency } = useStationeryProductPricingContext(false);

  const discount = cardPromotionToDiscount(promotion);
  const quantity = cardConfig.customizations.quantity;
  const discountedPrice = priceCalculator.getProductPrice(product.id, quantity, options, includeShipping ? deliveryInfo?.deliveryMethodId : undefined, discount, salesTax);

  // We decided line items do no show the discounted price (because customers don't see a strikethough, they think it's normal price). The discount is only shown in the total.
  const nonDiscountedPrice = priceCalculator.getProductPrice(product.id, quantity, options, includeShipping ? deliveryInfo?.deliveryMethodId : undefined, undefined, salesTax);

  const currencyOptions = orderCurrency ? { ...PriceSummaryCurrencyFormatOptions, currency: orderCurrency } : PriceSummaryCurrencyFormatOptions;

  return {
    ...useLineItemsByStep(product, lineItemSteps, nonDiscountedPrice.lineItems),
    total: minorUnitsToPriceString(discountedPrice.discounts?.preDiscountPriceInMinorUnits ?? discountedPrice.totalPriceInMinorUnits, currencyOptions),
    totalInMinorUnits: discountedPrice.unitPriceInMinorUnits, // not the total, actually unit price
    pricePerCard: minorUnitsToPriceString(discountedPrice.unitPriceInMinorUnits, currencyOptions),
    discountedTotal: discountedPrice.discounts ? minorUnitsToPriceString(discountedPrice.totalPriceInMinorUnits, currencyOptions) : null,
    discountPercentage: discount?.type === 'percent' ? discount.percentage : null,
  };
};
